<template>
  <div class="stepper-container" :class="{ 'stepper-container--glass': glass }">
    <div class="stepper-wrapper" :class="{ 'stepper-vertical': orientation === 'vertical' }">
      <div
        v-for="(step, index) in steps"
        :key="`${step.title}-${index}`"
        class="stepper-step"
        :class="getStepClass(index)"
        :style="stepStyle(index)"
        @click="handleStepClick(index)"
      >
        <!-- Step Circle -->
        <div class="stepper-circle">
          <span v-if="isStepCompleted(index)" class="stepper-check-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
                fill="currentColor"
              />
            </svg>
          </span>
          <span v-else class="stepper-number">{{ index + 1 }}</span>
        </div>

        <!-- Step Label -->
        <div class="stepper-label">
          <span class="stepper-title">{{ step.title }}</span>
          <span v-if="step.description" class="stepper-description">
            {{ step.description }}
          </span>
        </div>

        <!-- Connector Line -->
        <div
          v-if="index < steps.length - 1"
          class="stepper-connector"
          :class="{ 'stepper-connector-active': isStepCompleted(index) }"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Stepper Component
 * 
 * A reusable stepper/wizard component for multi-step processes.
 * 
 * @example
 * // Basic usage
 * <Stepper 
 *   :steps="steps" 
 *   :current-step="currentStep"
 * />
 * 
 * @example
 * // With clickable steps
 * <Stepper 
 *   :steps="steps" 
 *   v-model:current-step="currentStep"
 *   :clickable="true"
 *   @step-click="handleStepClick"
 * />
 * 
 * @example
 * // Steps data structure
 * const steps = [
 *   { title: 'Step 1', description: 'Description here' },
 *   { title: 'Step 2', description: 'Description here' },
 *   { title: 'Step 3' }
 * ]
 */
export default {
  name: 'Stepper',
  props: {
    steps: {
      type: Array,
      required: true,
      validator: (steps) => {
        return steps.every(
          (step) =>
            typeof step === 'object' &&
            step !== null &&
            'title' in step
        );
      },
    },
    currentStep: {
      type: Number,
      default: 0,
      validator: (value) => value >= 0,
    },
    clickable: {
      type: Boolean,
      default: false,
    },
    orientation: {
      type: String,
      default: 'horizontal',
      validator: (value) => ['horizontal', 'vertical'].includes(value),
    },
    /** Frosted glass styling, staggered motion, and active-step glow */
    glass: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['step-click', 'update:currentStep'],
  methods: {
    stepStyle(index) {
      return {
        '--step-stagger-delay': `${0.04 + index * 0.05}s`,
      };
    },
    isStepCompleted(index) {
      return index < this.currentStep;
    },
    isStepActive(index) {
      return index === this.currentStep;
    },
    isStepPending(index) {
      return index > this.currentStep;
    },
    getStepClass(index) {
      return {
        'stepper-step-active': this.isStepActive(index),
        'stepper-step-completed': this.isStepCompleted(index),
        'stepper-step-pending': this.isStepPending(index),
        'stepper-step-clickable': this.clickable,
      };
    },
    handleStepClick(index) {
      if (this.clickable && index !== this.currentStep) {
        this.$emit('step-click', index);
        this.$emit('update:currentStep', index);
      }
    },
  },
};
</script>

<style scoped>
@import './Stepper.css';
</style>

